import uniq from 'lodash/uniq';
import { get, post, put } from '../../../utils/api';

const MESSAGE_LIMIT = 50;

type FetchMessagesParams = {
  conversationId: string,
  cursor?: string,
  includeUser?: boolean,
  includeAttachments?: boolean,
};

type SendMessageParams = {
  message: string,
  conversationId: string,
  recipient: {
    prospectId?: string,
    customerId?: string,
    householdId?: string,
    contactId?: string,
  },
};

type UpdateConversationStatusParams = {
  conversationId: string,
  conversationStatus: 'RESOLVED' | 'UNRESOLVED',
  propertyId: string,
};

export async function fetchMessages({
  conversationId,
  cursor,
  includeUser,
  includeAttachments,
}: FetchMessagesParams): Promise<Array<any>> {
  if (!conversationId) return [];

  const params = new URLSearchParams();
  params.append('conversationId', conversationId);
  params.append('limit', MESSAGE_LIMIT.toString());

  if (cursor) {
    params.append('cursor', cursor);
  }

  const messages = await get(
    `${
      import.meta.env.REACT_APP_TWO_WAY_API_URL || ''
    }/messages?${params.toString()}`,
    { isOtherApi: true },
  );

  // Fetch user information if requested
  const messagesWithUser = messages.filter((m) => m.userId);
  const userIds = uniq(messagesWithUser.map((m) => m.userId));

  const users =
    includeUser && userIds.length
      ? await get(`/users?id=${userIds.join(',')}`)
      : [];

  return messages.map((m) => ({
    ...m,
    user: users.find((u) => u.id === m.userId),
  }));
}

export async function sendMessage({
  message,
  conversationId,
  recipient,
}: SendMessageParams): Promise<any> {
  const response = await post(
    `${import.meta.env.REACT_APP_TWO_WAY_API_URL || ''}/messages`,
    JSON.stringify({
      content: message,
      conversationId,
      recipient,
    }),
    // The full body carries the consent-block code that useSendMessage branches on.
    { isOtherApi: true, fullError: true },
  );

  return response;
}

export async function updateConversationStatus({
  conversationId,
  conversationStatus,
  propertyId,
}: UpdateConversationStatusParams): Promise<any> {
  return put(
    `${
      import.meta.env.REACT_APP_TWO_WAY_API_URL || ''
    }/conversations/${conversationId}`,
    JSON.stringify({
      conversationStatus,
      propertyId,
    }),
    { isOtherApi: true },
  );
}

export function fetchCommunicationPreferencesByCustomer({ customerId }) {
  return get(`/customers/${customerId}/communication-preferences`);
}

export function fetchCommunicationPreferencesByProspect({ prospectId }) {
  return get(`/prospects/${prospectId}/communication-preferences`);
}
